<script setup lang="ts">

import {nextTick, onMounted, Ref, ref, watch, computed} from "vue";
import Multiselect from "vue-multiselect";
import BtnTab from "../Utils/BtnTab.vue";
import PlanningTab from "./Utils/PlanningTab.vue";
import type EpisodRef from "./Models/EpisodRef";
import type Week from "./Models/Week";
import EpisodRefs from "./Models/EpisodRefs";
import Session from "./Models/Session";
import LogoEpisod from "./Utils/LogoEpisod.vue";
import { useSportSessions } from '@/composables/useSportSessions';

const { sessions: sportSessions, loadSessions } = useSportSessions();

const mySessions = computed(() => {
    if (!sportSessions.value) return [];
    return [...sportSessions.value.upcoming, ...sportSessions.value.past];
});

const refs = ref<EpisodRefs>({
    weeks: [],
    studios: [],
    coaches: [],
    hubs: [],
    sports: [],
});

const search = ref<{
    week: Week|null,
    hubs: EpisodRef[],
    studios: EpisodRef[],
}>({
    week: null,
    hubs: [],
    studios: [],
})

const planning = ref<{[key: string]: Session[]}>({});
const planningLoading = ref(false);

onMounted(() => {
    loadSessions();
    fetch('/episod/refs/load')
        .then(response => response.json())
        .then(data => {
            refs.value = data;

            search.value.week = refs.value.weeks[0];

            search.value.hubs = refs.value.hubs.filter((hub: EpisodRef) => {
                return hub.key === 'nation' || hub.key === 'episod-republique';
            })
            search.value.studios = refs.value.studios.filter((studio: EpisodRef) => {
                return studio.key === 'cat_athletic' || studio.key === 'cat_lift'  || studio.key === 'cat_reformer' ;
            })
        })
})

watch(search, function() {
    planningLoading.value = true;


    fetch('/episod/planning', {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            dates: Object.values(search.value.week?.days),
            hubs: search.value.hubs.map(hub => hub.key),
            rooms: search.value.studios.map(sport => sport.key),
        })
    })
        .then(response => response.json())
        .then(data => {
            const activeTab = document.querySelector('#tab-planning button.active');
            planning.value = data;
            planningLoading.value = false;

            let activeDay: null|string = null
            if (Object.keys(planning.value).length > 0) {
                if (activeTab) {
                    activeDay = activeTab.getAttribute('id').split('-')[0];
                    if (planning.value[search.value.week.days[activeDay]] === undefined) {
                        activeDay = null;
                    }
                }
            }

            if (activeDay === null) {
                const days = Object.keys(search.value.week.days);
                const dates = Object.values(search.value.week.days);
                const planningDates = Object.keys(planning.value);
                if (planningDates.length > 0) {
                    activeDay = days[dates.indexOf(planningDates[0])];
                }
            }
            if (activeDay) {
                nextTick(() => {
                    let button = document.querySelector('#'+activeDay+'-tab');
                    button.classList.remove("active")
                    bootstrap.Tab.getOrCreateInstance(button).show()
                });
            }
        })
}, {deep: true});

</script>

<template>
<div>
    <header>
        <div class="container p-4">
            <logo-episod />
        </div>
    </header>


    <main class="container-fluid mt-3">
        <div>
            <form @submit="e => e.preventDefault()">
                <div class="row">
                    <div class="col-lg-3 mb-3">
                        <multiselect :options="refs.weeks" label="label" :multiple="false" v-model="search.week" />
                    </div>
                    <div class="col-lg-3  mb-3">
                        <multiselect :options="refs.hubs" label="label" track-by="key" multiple v-model="search.hubs" />
                    </div>
                </div>
            </form>
        </div>


        <div v-if="search.week">
            <ul class="nav nav-tabs nav-fill" id="tab-planning" role="tablist">
                <btn-tab tab-id="monday" label="Lundi" :disabled="planning[search.week.days['monday']] === undefined"/>
                <btn-tab tab-id="tuesday" label="Mardi"  :disabled="planning[search.week.days['tuesday']] === undefined"/>
                <btn-tab tab-id="wednesday" label="Mercredi"  :disabled="planning[search.week.days['wednesday']] === undefined"/>
                <btn-tab tab-id="thursday" label="Jeudi"  :disabled="planning[search.week.days['thursday']] === undefined"/>
                <btn-tab tab-id="friday" label="Vendredi"  :disabled="planning[search.week.days['friday']] === undefined"/>
                <btn-tab tab-id="saturday" label="Samedi"  :disabled="planning[search.week.days['saturday']] === undefined"/>
                <btn-tab tab-id="sunday" label="Dimanche"  :disabled="planning[search.week.days['sunday']] === undefined"/>
            </ul>
            <div class="tab-content" id="tab-planning-content">
                <div v-if="planningLoading" class="spinner-border" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <planning-tab v-if="planning[search.week.days['monday']] !== undefined" :planning="planning[search.week.days['monday']]" :my-sessions="mySessions" :studios="search.studios" :hubs="search.hubs" :refs="refs" :date="search.week.days.monday" tab-id="monday" />
                <planning-tab v-if="planning[search.week.days['tuesday']] !== undefined" :planning="planning[search.week.days['tuesday']]" :my-sessions="mySessions" :studios="search.studios" :hubs="search.hubs" :refs="refs" :date="search.week.days.tuesday" tab-id="tuesday" />
                <planning-tab v-if="planning[search.week.days['wednesday']] !== undefined" :planning="planning[search.week.days['wednesday']]" :my-sessions="mySessions" :studios="search.studios" :hubs="search.hubs" :refs="refs" :date="search.week.days.wednesday" tab-id="wednesday" />
                <planning-tab v-if="planning[search.week.days['thursday']] !== undefined" :planning="planning[search.week.days['thursday']]" :my-sessions="mySessions" :studios="search.studios" :hubs="search.hubs" :refs="refs" :date="search.week.days.thursday" tab-id="thursday" />
                <planning-tab v-if="planning[search.week.days['friday']] !== undefined" :planning="planning[search.week.days['friday']]" :my-sessions="mySessions" :studios="search.studios" :hubs="search.hubs" :refs="refs" :date="search.week.days.friday" tab-id="friday" />
                <planning-tab v-if="planning[search.week.days['saturday']] !== undefined" :planning="planning[search.week.days['saturday']]" :my-sessions="mySessions" :studios="search.studios" :hubs="search.hubs" :refs="refs" :date="search.week.days.saturday" tab-id="saturday" />
                <planning-tab v-if="planning[search.week.days['sunday']] !== undefined" :planning="planning[search.week.days['sunday']]" :my-sessions="mySessions" :studios="search.studios" :hubs="search.hubs" :refs="refs" :date="search.week.days.sunday" tab-id="sunday" />
            </div>
        </div>
    </main>
</div>
</template>

<style scoped>
header {
    background: #000;
    text-align: center;
    padding: 15px
}
</style>
